import { Button, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { StepActions, useStepperContext } from "src/components/stepper";
import { BidContractType, BidPackageStatus, BidScopeSource, SaveBidPackageInput } from "src/generated/graphqlTypes";
import { useSavePlanBasedBidPackagesMutation } from "src/routes/bids/plan-based-bid-package/PlanBasedBidPackagePage.generated";
import { createBidPackagesDetailUrl, createBidPackagesUrl, createPlanBasedBidPackageEditUrl } from "src/RouteUrls";
import { fail, formatList } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import {
  INTERNAL_ESTIMATE_ID,
  PlanBasedBidPackageFormObjectState,
  PlanBasedBidPackageFormState,
  PlanBasedBidPackageStep,
  mapToBidPlanGroupsInput,
} from "../utils";

type PlanBasedBidPackageStepActionsProps = { formState: PlanBasedBidPackageFormObjectState; isEdit: boolean };

export function PlanBasedBidPackageStepActions(props: PlanBasedBidPackageStepActionsProps) {
  const { formState, isEdit } = props;
  const { currentStep, nextStep, prevStep, steps, isLastStep } = useStepperContext();
  const [{ projectId }] = useQueryParams({ projectId: StringParam });
  const [saveBidPackages, { loading: savingBidPackages }] = useSavePlanBasedBidPackagesMutation();
  const navigate = useNavigate();
  const currentStepIndex = steps.findIndex((step) => step.value === currentStep.value);
  const nextStepDisabled = steps[currentStepIndex + 1]?.disabled;

  const save = useCallback(
    async (exiting: boolean) => {
      const input = mapToSaveBidPackagesInput(formState);
      const result = await saveBidPackages({ variables: { input: [input] } });
      const saved = result.data?.saveBidPackages.bidPackages?.first;
      const groupId = saved?.bidPackageGroup?.id;
      if (saved && groupId) {
        // Fold the saved server response back into the form. `update` re-runs the form's `init.map`
        // (mapBidPackageToForm) and reconciles field-by-field, so it both picks up the server-assigned
        // ids and marks the form clean (this also commits form changes so the stepper won't prompt about
        // unsaved changes).
        formState.update(saved);
        window.history.replaceState(null, "", createPlanBasedBidPackageEditUrl(groupId, projectId));
      }
      if (exiting) {
        const url = isEdit ? createBidPackagesDetailUrl(groupId!, saved!.id) : createBidPackagesUrl(projectId);
        navigate(url);
      } else {
        nextStep();
      }
    },
    [formState, saveBidPackages, projectId, isEdit, navigate, nextStep],
  );

  const submitBidPackage = useCallback(async () => {
    const bidPackageId = formState.value.bidPackageId ?? fail("Cannot submit a bid package that has not been saved");
    const groupId = formState.value.bidPackageGroupId ?? fail("Cannot submit a bid package that has not been saved");
    await saveBidPackages({ variables: { input: [{ id: bidPackageId, status: BidPackageStatus.Open }] } });
    navigate(createBidPackagesDetailUrl(groupId, bidPackageId));
  }, [formState, saveBidPackages, navigate]);

  const isFormValid = useComputed(() => formState.valid, [formState]);
  const continueDisabledReason = nextStepDisabled ? continueReasonByStep(currentStep.value, formState) : undefined;

  return (
    <StepActions>
      <>
        {currentStepIndex > 0 && <Button variant="tertiary" label="Back" size="lg" onClick={prevStep} />}
        <Button
          variant="secondary"
          label={isEdit ? "Save & Exit" : "Save Draft & Exit"}
          size="lg"
          onClick={() => save(true)}
          disabled={savingBidPackages}
        />
        {isLastStep ? (
          !isEdit && (
            <Button
              label="Create Bid Package"
              size="lg"
              onClick={submitBidPackage}
              disabled={savingBidPackages || currentStep.disabled || !isFormValid}
            />
          )
        ) : (
          <Button
            label="Continue"
            size="lg"
            onClick={() => save(false)}
            disabled={savingBidPackages || continueDisabledReason}
          />
        )}
      </>
    </StepActions>
  );
}

function continueReasonByStep(step: string, formState: ObjectState<PlanBasedBidPackageFormState>): string | undefined {
  if (step === PlanBasedBidPackageStep.DEFINE_PACKAGE) {
    return "Fill all required fields to continue.";
  }
  if (step === PlanBasedBidPackageStep.CONFIGURE_BID_GROUPS) {
    return formatList(formState.bidPlanGroups.errors);
  }
  return undefined;
}

function mapToSaveBidPackagesInput(formState: ObjectState<PlanBasedBidPackageFormState>): SaveBidPackageInput {
  const {
    bidPackageId,
    bidPackageGroupId,
    marketId,
    projects,
    costCodes,
    costType,
    internalEstimateType,
    productOfferings,
    requests,
    bidPlanGroups,
  } = formState.value;
  const internalEstimate = requests?.find((r) => r.tradePartnerId === INTERNAL_ESTIMATE_ID);
  return {
    id: bidPackageId ?? undefined,
    bidPackageGroupId: bidPackageGroupId ?? undefined,
    projects: marketId && (projects ?? []).isEmpty ? [marketId] : (projects ?? []),
    costCodes: costCodes ?? [],
    costType: costType ?? undefined,
    scopeSource: BidScopeSource.ProductOffering,
    isUnitBased: false,
    isPlanBasedV2: true,
    internalEstimateBidContract: internalEstimate
      ? {
          id: internalEstimate.id,
          isInternalEstimate: true,
          internalEstimateType,
          archive: internalEstimate.archive,
          type: BidContractType.UserCreatedGroupBased,
        }
      : undefined,
    productOfferings: productOfferings ?? undefined,
    requests: requests?.nonEmpty ? requests.filter((r) => r.tradePartnerId !== INTERNAL_ESTIMATE_ID) : undefined,
    bidPlanGroups: bidPlanGroups ? mapToBidPlanGroupsInput(bidPlanGroups) : undefined,
  };
}
